import React, { useMemo, useState } from 'react';
import { SearchParams, SpecialEvent } from '../types';
import { BrandLogo } from './BrandIdentity';
import { geocodeLocation, getBrowserLocation, normalizeLocationInput } from '../services/locationSearch';

interface LandingPageProps {
  events: SpecialEvent[];
  onSearch: (params: SearchParams) => void;
  onBusinessClick: () => void;
}

const VIBES = [
  { id: 'All Vibes', label: 'All Vibes' },
  { id: 'Craft Beer', label: 'Craft Beer' },
  { id: 'Cocktails', label: 'Cocktails' },
  { id: 'Trivia', label: 'Trivia' },
  { id: 'Live Music', label: 'Live Music' },
  { id: 'Cheap Eats', label: 'Cheap Eats' },
  { id: 'Patio', label: 'Patio' },
  { id: 'Rooftop', label: 'Rooftop' },
  { id: 'Speakeasy', label: 'Speakeasy' },
  { id: 'Dog Friendly', label: 'Dog Friendly' },
];

const RADIUS_OPTIONS = [5, 10, 25] as const;

const FAQS = [
  {
    question: 'Is Hoppy Hours free to use?',
    answer: 'Yes. Hoppy Hours is completely free for people looking to discover happy hours and specials nearby.'
  },
  {
    question: 'Can I search outside the original launch cities?',
    answer: 'Yes. Enter a ZIP code, city, or city and state and Hoppy Hours will look for nearby active listings.'
  },
  {
    question: 'How does it work for bars and restaurants?',
    answer: 'Venues can list their happy hour specials, upload photos, and manage their profile so locals can find them by location.'
  },
  {
    question: 'Is there a free trial for businesses?',
    answer: 'Yes. Businesses can start a 30-day free trial before deciding if Hoppy Hours is right for them.'
  }
];

function formatLocalDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const LandingPage: React.FC<LandingPageProps> = ({ events, onSearch, onBusinessClick }) => {
  const [date, setDate] = useState<string>(formatLocalDateForInput(new Date()));
  const [time, setTime] = useState<string>('16:00');
  const [location, setLocation] = useState('');
  const [radiusMiles, setRadiusMiles] = useState<5 | 10 | 25>(10);
  const [selectedVibe, setSelectedVibe] = useState('All Vibes');
  const [isAnyDate, setIsAnyDate] = useState(true);
  const [isAnyTime, setIsAnyTime] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const activeVenueCount = useMemo(() => {
    return new Set(events.map((event) => event.venueId || event.venueName).filter(Boolean)).size;
  }, [events]);

  const buildBaseParams = (nextLocation = normalizeLocationInput(location)): SearchParams => ({
    date: isAnyDate ? '' : date,
    time: isAnyTime ? '' : time,
    location: nextLocation,
    selectedCities: [],
    vibe: selectedVibe === 'All Vibes' ? undefined : selectedVibe,
    category: 'All',
    radiusMiles,
    coords: null,
    locationError: null,
  });

  const runSearchWithCoords = (locationText: string, lat: number, lng: number, label?: string) => {
    onSearch({
      ...buildBaseParams(locationText),
      location: locationText,
      locationLabel: label || locationText,
      coords: { lat, lng },
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanedLocation = normalizeLocationInput(location);

    if (!cleanedLocation) {
      setLocationError('Enter a ZIP code, city, or city and state.');
      return;
    }

    setIsSearching(true);
    setLocationError(null);

    const geocoded = await geocodeLocation(cleanedLocation);
    setIsSearching(false);

    if (!geocoded) {
      setLocationError('We could not find that location. Try a ZIP code or add the state, like Plymouth, MI.');
      return;
    }

    runSearchWithCoords(cleanedLocation, geocoded.lat, geocoded.lng, geocoded.label);
  };

  const handleUseCurrentLocation = async () => {
    try {
      setIsLocating(true);
      setLocationError(null);
      const current = await getBrowserLocation();
      setLocation('Current location');
      runSearchWithCoords('Current location', current.lat, current.lng, current.label);
    } catch (err: any) {
      setLocationError(err?.message || 'We could not access your current location. You can still search by ZIP or city.');
    } finally {
      setIsLocating(false);
    }
  };

  const handleStartExploring = () => {
    onSearch({
      date: '',
      time: '',
      location: '',
      selectedCities: [],
      category: 'All',
      vibe: undefined,
      radiusMiles: 10,
      coords: null,
      locationError: null,
    });
  };

  const getButtonText = () => {
    if (isSearching) return 'SEARCHING...';
    if (selectedVibe === 'All Vibes') return 'FIND NEARBY DEALS';
    return `FIND ${selectedVibe.toUpperCase()} DEALS`;
  };

  return (
    <div className="relative min-h-screen bg-[#FFFDF5]">
      <section className="relative min-h-screen overflow-visible flex items-center justify-center py-20 px-4">
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[20s] scale-105"
          style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1543007630-9710e4a00a20?q=80&w=2070&auto=format&fit=crop")' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/70 to-[#FFFDF5] z-10" />

        <div className="relative z-20 w-full max-w-7xl text-center">
          <div className="animate-in fade-in zoom-in duration-1000 flex flex-col items-center">
            <div className="mt-12 md:mt-24 mb-6 md:mb-10 w-full px-4">
              <BrandLogo size="hero" withSlogan light className="group" />
            </div>

            <div className="w-full max-w-5xl mx-auto space-y-6 md:space-y-8 px-2 md:px-0">
              <form
                onSubmit={handleSubmit}
                className="relative z-20 p-4 md:p-6 bg-black/85 backdrop-blur-3xl rounded-[2.5rem] md:rounded-[4rem] border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.6)]"
              >
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_0.8fr_0.8fr] gap-3 md:gap-4">
                    <div className="relative min-w-0">
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-amber-500/60">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        value={location}
                        onChange={(e) => {
                          setLocation(e.target.value);
                          if (locationError) setLocationError(null);
                        }}
                        placeholder="ZIP, city, or city/state"
                        className="w-full h-14 md:h-20 bg-white/5 text-amber-400 border-2 border-transparent rounded-2xl md:rounded-[3rem] pl-14 md:pl-16 pr-6 md:pr-8 focus:bg-white/10 focus:border-amber-500/30 transition-all font-black text-base md:text-xl placeholder-white/25 outline-none"
                      />
                    </div>

                    <div className={`relative bg-black rounded-2xl md:rounded-[3rem] border-2 transition-all h-14 md:h-20 flex items-center shadow-2xl ${isAnyDate ? 'border-amber-500/60 bg-amber-500/10' : 'border-amber-500/20'}`}>
                      {isAnyDate ? (
                        <div className="flex-1 flex items-center pl-8 text-amber-500 font-black text-base md:text-sm uppercase tracking-[0.2em] whitespace-nowrap overflow-hidden pr-16">Any Date</div>
                      ) : (
                        <input
                          type="date"
                          value={date}
                          onChange={(e) => setDate(e.target.value)}
                          className="flex-1 bg-transparent text-amber-500 font-black pl-8 pr-16 outline-none text-base w-full h-full [color-scheme:dark]"
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => setIsAnyDate(!isAnyDate)}
                        className={`absolute right-3 w-12 h-9 rounded-xl text-[9px] font-black transition-all z-10 shadow-lg ${isAnyDate ? 'bg-amber-500 text-black' : 'bg-white/10 text-white/30 hover:bg-white/20'}`}
                      >
                        {isAnyDate ? 'X' : 'ANY'}
                      </button>
                    </div>

                    <div className={`relative bg-black rounded-2xl md:rounded-[3rem] border-2 transition-all h-14 md:h-20 flex items-center shadow-2xl ${isAnyTime ? 'border-amber-500/60 bg-amber-500/10' : 'border-amber-500/20'}`}>
                      {isAnyTime ? (
                        <div className="flex-1 flex items-center pl-8 text-amber-500 font-black text-base md:text-sm uppercase tracking-[0.2em] whitespace-nowrap overflow-hidden pr-16">Anytime</div>
                      ) : (
                        <input
                          type="time"
                          value={time}
                          onChange={(e) => setTime(e.target.value)}
                          className="flex-1 bg-transparent text-amber-500 font-black pl-8 pr-16 outline-none text-base w-full h-full [color-scheme:dark]"
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => setIsAnyTime(!isAnyTime)}
                        className={`absolute right-3 w-12 h-9 rounded-xl text-[9px] font-black transition-all z-10 shadow-lg ${isAnyTime ? 'bg-amber-500 text-black' : 'bg-white/10 text-white/30 hover:bg-white/20'}`}
                      >
                        {isAnyTime ? 'X' : 'ANY'}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                    <div className="grid grid-cols-3 gap-2 bg-white/5 rounded-2xl md:rounded-[3rem] p-2 border border-white/10">
                      {RADIUS_OPTIONS.map((miles) => (
                        <button
                          key={miles}
                          type="button"
                          onClick={() => setRadiusMiles(miles)}
                          className={`h-11 md:h-12 rounded-2xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${radiusMiles === miles ? 'bg-amber-500 text-black shadow-xl' : 'text-white/55 hover:text-white hover:bg-white/10'}`}
                        >
                          {miles} mi
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleUseCurrentLocation}
                      disabled={isLocating}
                      className="h-14 md:h-auto px-6 rounded-2xl md:rounded-[3rem] bg-white/10 text-white font-black text-[10px] uppercase tracking-widest hover:bg-white hover:text-black transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isLocating ? 'Locating...' : 'Use My Location'}
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isSearching}
                    className="w-full h-14 md:h-16 bg-amber-500 hover:bg-amber-400 text-black font-black text-sm md:text-lg rounded-2xl md:rounded-[3rem] transition-all active:scale-[0.98] shadow-2xl uppercase tracking-widest text-center disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {getButtonText()}
                  </button>

                  {locationError && (
                    <div className="px-5 py-3 rounded-2xl bg-red-500/10 border border-red-400/30 text-red-100 text-sm font-bold text-left">
                      {locationError}
                    </div>
                  )}
                </div>
              </form>
            </div>

            <div className="mt-8 md:mt-12 w-full max-w-6xl px-2">
              <div className="flex flex-col items-center gap-6">
                <span className="text-xs md:text-sm font-black text-amber-500 uppercase tracking-[0.4em] drop-shadow-[0_2px_8px_rgba(0,0,0,1)]">Find your golden vibe</span>
                <div className="w-full bg-zinc-900/40 backdrop-blur-3xl rounded-[3rem] border border-white/10 shadow-2xl px-2 py-2 overflow-hidden">
                  <div className="flex gap-2 md:gap-3 overflow-x-auto no-scrollbar items-center justify-start px-2 md:px-8 py-1">
                    {VIBES.map((vibe) => (
                      <button
                        key={vibe.id}
                        type="button"
                        onClick={() => setSelectedVibe(vibe.id)}
                        className={`shrink-0 px-4 py-2 md:px-6 md:py-2.5 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all duration-300 border ${selectedVibe === vibe.id ? 'bg-amber-500 text-black border-amber-500 shadow-[0_0_25px_rgba(245,158,11,0.4)] scale-105' : 'bg-zinc-800/80 text-white/80 border-transparent hover:bg-zinc-700 hover:text-white'}`}
                      >
                        {vibe.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section className="relative min-h-[600px] lg:min-h-0 lg:py-32 px-4 sm:px-6 bg-[#FFFDF5] border-y border-black/[0.03] grainy overflow-hidden flex flex-col">
        <div className="max-w-7xl mx-auto w-full flex-1 flex flex-col relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-24 items-stretch lg:items-center text-left flex-1 relative">
            <div className="absolute lg:relative inset-0 lg:inset-auto z-0 lg:z-10 animate-in slide-in-from-right-10 duration-1000 order-1 lg:order-2">
              <div className="absolute -inset-4 bg-amber-500/10 rounded-none lg:rounded-[4rem] rotate-0 lg:rotate-3 -z-10 hidden lg:block" />
              <div className="h-full lg:h-[600px] overflow-hidden shadow-none lg:shadow-2xl border-0 lg:border-4 border-white lg:rounded-[4rem]">
                <img
                  src="https://images.unsplash.com/photo-1575444758702-4a6b9222336e?q=80&w=2070&auto=format&fit=crop"
                  className="w-full h-full object-cover transition-transform duration-[10s] hover:scale-110 opacity-70 lg:opacity-100 brightness-[0.25] lg:brightness-[0.9]"
                  alt="Crowded Happy Hour Bar"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40 lg:hidden" />
              </div>
            </div>

            <div className="relative z-10 px-6 py-20 lg:py-0 lg:px-0 flex flex-col justify-end lg:justify-center h-full lg:h-auto animate-in slide-in-from-left-10 duration-1000 order-2 lg:order-1">
              <div className="space-y-8 lg:space-y-10">
                <div className="space-y-4 lg:space-y-6">
                  <span className="inline-flex items-center gap-3 px-6 py-2 bg-amber-500/10 rounded-full border border-amber-500/20 backdrop-blur-md lg:backdrop-blur-none">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    <span className="text-[10px] font-black uppercase tracking-[0.4em] text-amber-500 lg:text-amber-600">Venue Owners</span>
                  </span>
                  <h2 className="text-5xl lg:text-8xl font-black serif tracking-tighter leading-[0.85] text-white lg:text-black">
                    Turn Happy Hour Into Your <span className="italic text-amber-500 lg:text-amber-600 underline lg:decoration-amber-200 decoration-amber-500 decoration-4 underline-offset-8">Busiest Hour.</span>
                  </h2>
                </div>

                <div className="space-y-8">
                  <p className="text-xl lg:text-3xl text-gray-300 lg:text-gray-500 font-medium leading-relaxed max-w-2xl">
                    Reach locals right when they’re choosing where to go. List your specials, showcase your vibe, and track performance with verified exposure.
                  </p>

                  <div className="pt-4 flex flex-col items-start gap-4">
                    <button
                      onClick={onBusinessClick}
                      className="group relative px-8 py-5 bg-amber-500 rounded-full flex items-center justify-between gap-6 lg:gap-12 hover:bg-white transition-all duration-500 shadow-[0_20px_40px_rgba(245,158,11,0.25)] hover:shadow-[0_15px_30px_rgba(245,158,11,0.15)] active:scale-[0.98] ring-4 ring-amber-500/10 border-2 border-transparent hover:border-amber-500/20"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-amber-500 shadow-md group-hover:rotate-12 transition-transform duration-500 shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-sm lg:text-xl font-black text-black uppercase tracking-tight">List Your Happy Hour Now</span>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center group-hover:bg-black transition-all shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-black group-hover:text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </button>
                    <p className="text-[10px] lg:text-xs font-bold text-gray-400 lg:text-gray-400 uppercase tracking-widest pl-4">Start your 30-day free trial today - takes less than 5 minutes to list your happy hour.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-4 sm:px-6 bg-[#FFFDF5]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="relative rounded-[2rem] md:rounded-[3rem] overflow-hidden border-4 border-white shadow-2xl min-h-[360px] md:min-h-[520px]">
            <img
              src="https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?q=80&w=1974&auto=format&fit=crop"
              alt="Cocktail on bar"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          <div className="text-left space-y-8">
            <span className="inline-flex items-center gap-3 px-6 py-2 rounded-full border border-amber-300 text-amber-600 font-black text-[10px] uppercase tracking-[0.4em]">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              {activeVenueCount} active venues and growing
            </span>

            <h2 className="text-5xl lg:text-8xl font-black serif tracking-tighter leading-[0.85] text-black">
              Your neighborhood.
              <br />
              Your <span className="italic text-amber-600 underline decoration-amber-200 decoration-4 underline-offset-8">deals.</span>
            </h2>

            <p className="text-xl lg:text-3xl text-gray-500 font-medium leading-relaxed max-w-2xl">
              Discover the hidden gems and local favorites right in your backyard. Search by ZIP, city, or city and state, then pick the radius that fits your night.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={handleStartExploring}
                className="px-8 py-5 bg-black text-white rounded-full font-black text-xs uppercase tracking-widest hover:bg-amber-500 hover:text-black transition-all shadow-xl"
              >
                Browse All Deals
              </button>
              <button
                type="button"
                onClick={onBusinessClick}
                className="px-8 py-5 bg-amber-500 text-black rounded-full font-black text-xs uppercase tracking-widest hover:bg-white transition-all shadow-xl border border-amber-200"
              >
                List Your Business
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40 px-6 bg-[#080808] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(245,158,11,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.18) 1px, transparent 1px)', backgroundSize: '64px 64px' }} />
        <div className="max-w-6xl mx-auto text-center space-y-10 relative z-10">
          <span className="inline-flex items-center gap-3 px-6 py-2 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 font-black text-[10px] uppercase tracking-[0.4em]">
            Hoppy Dispatch
          </span>
          <h2 className="text-5xl sm:text-7xl lg:text-[8rem] font-black serif tracking-tighter leading-[0.85]">
            Your next golden hour might be <span className="italic text-amber-500">one ZIP away.</span>
          </h2>
          <p className="text-gray-300 font-medium text-lg md:text-3xl leading-relaxed max-w-4xl mx-auto">
            Follow the deals, chase the patio seats, and let the map do the planning. Pick 5, 10, or 25 miles and we’ll surface the nearby spots worth raising a glass to.
          </p>

          <div className="pt-4 flex flex-col items-center gap-6">
            <p className="text-[10px] md:text-xs font-black uppercase tracking-[0.35em] text-amber-400/90">
              Fresh specials, new venues, and local finds land on our socials first
            </p>
            <div className="flex items-center justify-center gap-4 md:gap-6">
              <a
                href="https://www.facebook.com/profile.php?id=61586567274182"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow Hoppy Hours on Facebook"
                className="w-14 h-14 md:w-16 md:h-16 rounded-full border border-amber-500/50 bg-amber-500 text-black hover:bg-white transition-all duration-300 flex items-center justify-center shadow-[0_0_35px_rgba(245,158,11,0.25)]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 md:w-7 md:h-7">
                  <path d="M13 9h3V6h-3c-2.8 0-5 2.2-5 5v2H6v3h2v6h3v-6h3l1-3h-4v-2c0-1.1.9-2 2-2z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/hoppyhoursapp"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow Hoppy Hours on Instagram"
                className="w-14 h-14 md:w-16 md:h-16 rounded-full border border-amber-500/50 bg-amber-500 text-black hover:bg-white transition-all duration-300 flex items-center justify-center shadow-[0_0_35px_rgba(245,158,11,0.25)]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 md:w-7 md:h-7">
                  <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm11 1.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM12 7a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40 px-6 bg-[#FFFDF5] relative border-t border-black/[0.03] overflow-hidden">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-16 md:mb-24">
            <h2 className="text-4xl md:text-7xl font-black serif tracking-tight mb-6">Common <span className="italic text-amber-600">Questions.</span></h2>
            <p className="text-gray-500 font-medium uppercase tracking-[0.2em] text-[10px] md:text-xs">Everything you need to know about Hoppy Hours</p>
          </div>

          <div className="space-y-4">
            {FAQS.map((faq, index) => (
              <div
                key={faq.question}
                className="bg-white border border-black/[0.05] rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 grainy text-left"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-8 py-6 md:py-8 text-left flex justify-between items-center group"
                >
                  <span className="text-sm md:text-xl font-bold text-black tracking-tight group-hover:text-amber-600 transition-colors">
                    {faq.question}
                  </span>
                  <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-500 ${openFaqIndex === index ? 'bg-amber-500 text-black rotate-180' : 'bg-gray-50 text-gray-300'}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${openFaqIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-8 pb-8 md:pb-10 pt-2 text-gray-500 text-base md:text-lg font-medium Hyde leading-relaxed max-w-3xl">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
